import React, { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Save } from 'lucide-react';
import Layout from '../components/layout/Layout';
import Timer from '../components/test/Timer';
import CategoryNavigation from '../components/test/CategoryNavigation';
import QuestionCard from '../components/test/QuestionCard';
import Button from '../components/common/Button';
import ProgressBar from '../components/common/ProgressBar';
import { useTestStore } from '../store/testStore';
import MissedQuestionsNotification from '../components/test/MissedQuestionsNotification';

const TestPage: React.FC = () => {
  const navigate = useNavigate();
  const { 
    categories, 
    currentCategoryIndex, 
    nextCategory, 
    previousCategory, 
    isTestStarted,
    isTestCompleted,
    completeTest,
    answers
  } = useTestStore();
  
  const [showMissedNotification, setShowMissedNotification] = useState(false);
  const [missedQuestions, setMissedQuestions] = useState({ count: 0, category: '' });
  const [isCompleting, setIsCompleting] = useState(false);
  const notificationIntervalRef = useRef<NodeJS.Timeout>();
  const notificationTimeoutRef = useRef<NodeJS.Timeout>();
  const lastNotificationTimeRef = useRef<number>(0);
  
  const currentCategory = categories[currentCategoryIndex];
  
  useEffect(() => {
    if (!isTestStarted && !isTestCompleted) {
      navigate('/');
      return;
    }
    
    if (isTestCompleted) {
      navigate('/results');
      return;
    }
  }, [isTestStarted, isTestCompleted, navigate]);

  useEffect(() => {
    const currentCategoryQuestions = currentCategory?.questions || [];
    const unansweredCount = currentCategoryQuestions.filter(
      q => answers[q.id] === undefined
    ).length;

    // Clear existing intervals/timeouts
    if (notificationIntervalRef.current) {
      clearInterval(notificationIntervalRef.current);
    }
    if (notificationTimeoutRef.current) {
      clearTimeout(notificationTimeoutRef.current);
    }

    if (unansweredCount > 0) {
      const showNotification = () => {
        const now = Date.now();
        // Only show if at least 30 seconds passed since last notification
        if (now - lastNotificationTimeRef.current >= 30000 || lastNotificationTimeRef.current === 0) {
          setMissedQuestions({
            count: unansweredCount,
            category: currentCategory.title
          });
          setShowMissedNotification(true);
          lastNotificationTimeRef.current = now;

          // Auto-hide after 5 seconds
          notificationTimeoutRef.current = setTimeout(() => {
            setShowMissedNotification(false);
          }, 5000);
        }
      };

      // Show initial notification immediately if there are missed questions
      showNotification();

      // Set up periodic check (every 10 seconds)
      notificationIntervalRef.current = setInterval(() => {
        const newUnansweredCount = currentCategory.questions.filter(
          q => answers[q.id] === undefined
        ).length;
        
        if (newUnansweredCount > 0) {
          showNotification();
        }
      }, 10000);
    } else {
      setShowMissedNotification(false);
    }

    return () => {
      if (notificationIntervalRef.current) {
        clearInterval(notificationIntervalRef.current);
      }
      if (notificationTimeoutRef.current) {
        clearTimeout(notificationTimeoutRef.current);
      }
    };
  }, [currentCategoryIndex, categories, answers, currentCategory]);

  // Calculate progress
  const answeredQuestions = Object.keys(answers).length;
  const totalQuestions = categories.reduce((total, cat) => total + cat.questions.length, 0);
  const progress = (answeredQuestions / totalQuestions) * 100;
  
  // Calculate if all questions in current category are answered
  const currentCategoryAnswered = currentCategory?.questions.filter(
    q => answers[q.id] !== undefined
  ).length || 0;
  
  // Check if all categories are complete
  const allCategoriesComplete = categories.every(category => 
    category.questions.every(question => answers[question.id] !== undefined)
  );
  
  const handleTimeEnd = async () => {
    setIsCompleting(true);
    try {
      await completeTest();
    } catch (error) {
      console.error('Error completing test on time end:', error);
      setIsCompleting(false);
    }
  };
  
  const handleCompleteTest = async () => {
    setIsCompleting(true);
    try {
      await completeTest();
    } catch (error) {
      console.error('Error completing test:', error);
      setIsCompleting(false);
      navigate('/results');
    }
  };

  const handleCategoryChange = (newIndex: number) => {
    const unansweredCount = currentCategory?.questions.filter(
      q => answers[q.id] === undefined
    ).length || 0;

    if (unansweredCount > 0) {
      setMissedQuestions({
        count: unansweredCount,
        category: currentCategory.title
      });
      setShowMissedNotification(true);
      
      // Auto-hide after 5 seconds
      setTimeout(() => {
        setShowMissedNotification(false);
      }, 5000);
    }

    if (newIndex > currentCategoryIndex) {
      nextCategory();
    } else {
      previousCategory();
    }
  };
  
  if (!currentCategory) return null;
  
  return (
    <Layout>
      <MissedQuestionsNotification
        show={showMissedNotification}
        categoryTitle={missedQuestions.category}
        missedCount={missedQuestions.count}
        onClose={() => setShowMissedNotification(false)}
      />
      
      <div className="container-custom py-8">
        <div className="mb-6">
          <div className="flex justify-between items-center mb-3">
            <h2 className="text-2xl font-bold text-gray-800">Export Readiness Test</h2>
            <div className="text-sm text-gray-500">
              Questions Answered: <span className="font-medium">{answeredQuestions}/{totalQuestions}</span>
            </div>
          </div>
          
          <ProgressBar value={answeredQuestions} max={totalQuestions} showLabel />
        </div>
        
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Left Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 space-y-6">
              <Timer onTimeEnd={handleTimeEnd} />
              <CategoryNavigation />
            </div>
          </div>
          
          {/* Main Content */}
          <div className="lg:col-span-3">
            <motion.div
              key={currentCategoryIndex}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className="space-y-8"
            >
              <div className="bg-white rounded-xl shadow-card p-6">
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                  {currentCategory.title}
                </h2>
                <p className="text-gray-600 mb-4">
                  {currentCategory.description}
                </p>
                
                <div className="flex justify-between items-center">
                  <div className="text-sm text-gray-500">
                    Category {currentCategoryIndex + 1} of {categories.length}
                  </div>
                  <div className="text-sm text-gray-500">
                    {currentCategoryAnswered}/{currentCategory.questions.length} questions answered
                  </div>
                </div>
              </div>
              
              <div className="space-y-6">
                {currentCategory.questions.map((question, index) => (
                  <QuestionCard 
                    key={question.id} 
                    question={question} 
                    questionNumber={index + 1} 
                  />
                ))}
              </div>
              
              <div className="flex justify-between mt-8">
                {currentCategoryIndex > 0 && (
                  <Button
                    variant="outline"
                    leftIcon={<ArrowLeft />}
                    onClick={() => handleCategoryChange(currentCategoryIndex - 1)}
                    disabled={isCompleting}
                  >
                    Previous Category
                  </Button>
                )}
                
                {currentCategoryIndex === categories.length - 1 ? (
                  <Button
                    variant="primary"
                    rightIcon={<Save />}
                    onClick={handleCompleteTest}
                    disabled={!allCategoriesComplete || isCompleting}
                  >
                    {isCompleting ? 'Completing Test...' : 'Complete Test'}
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    rightIcon={<ArrowRight />}
                    onClick={() => handleCategoryChange(currentCategoryIndex + 1)}
                    disabled={isCompleting}
                  >
                    Next Category
                  </Button>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default TestPage;